import { Action, Actions } from '@/ai-elements/actions';
import { Shimmer } from '@/ai-elements/shimmer';
import brandclub_colors from '@/colors/brandclubColors';
import { CopyIcon, ShareIcon, ThumbsDownIcon, ThumbsUpIcon } from '@/icons';

const RetryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
    <path
      d="M3.502 16.666v-3.333c0-.367.298-.665.665-.665H7.5l.134.014a.665.665 0 0 1 0 1.303l-.134.013H5.475A6.12 6.12 0 0 0 10 16.001c3.06 0 5.586-2.29 5.955-5.25l.03-.131a.665.665 0 0 1 1.29.295l-.05.337A7.33 7.33 0 0 1 10 17.332a7.46 7.46 0 0 1-5.168-2.085v1.42a.665.665 0 0 1-1.33 0m.543-7.417a.665.665 0 0 1-1.32-.165zM10 2.67c1.994 0 3.837.797 5.178 2.093V3.333a.665.665 0 0 1 1.33 0v3.333a.665.665 0 0 1-.665.665H12.51a.665.665 0 0 1 0-1.33h2.015A6.12 6.12 0 0 0 10 3.998 6.003 6.003 0 0 0 4.045 9.25l-.66-.083-.66-.082A7.333 7.333 0 0 1 10 2.668"
      fill="currentColor"
    />
  </svg>
);

import React, { useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { ImagePreviewOverlay } from './ImagePreviewOverlay';
import { useIsMobile } from './ConciergeChatAreaV2/useIsMobile';
import { toast } from 'sonner';
import { getHeadersWithUniqueVisitorId } from '../../Auth';
import { getRoutePath } from '../../utils/domain';
import { sendMessageFeedback } from '../api';
import { SectionBasedProduct } from '../types';
import { ConciergeOrderData } from '../types/conversation';
import type { ConciergeDevInfo } from '../types/devOptions';
import { marked } from 'marked';
import type { ConciergeMessagePart as ConciergeMessagePartType } from '../types/conciergeToolTypes';
import { ConciergeMessagePart } from './ConciergeMessagePart';
import DevInfoPanel from './DevInfoPanel';
import { OrderCardList } from './OrderCard';
import { useSupportCopy } from './SupportThemeProvider';

import {
  MessageBubbleWidget,
  MessageBubbleWidgetState,
  ChainOfThoughtWidget,
  ChainOfThoughtWidgetState,
  FollowUpQueriesWidget,
  FollowUpQueriesWidgetState,
  customColor,
  themeColor,
} from '@brandclub/bc-widgets';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  bottomRef?: React.RefObject<HTMLDivElement>;
  isTyping?: boolean;
  isStreaming?: boolean;
  isLastMessage?: boolean;
  messageIndex?: number;
  conversationId?: string;
  feedback?: { type: 'like' | 'dislike'; timestamp: string };
  followUpQueries?: string[];
  /** The next user message content (used to derive which follow-up was clicked) */
  nextUserMessage?: string;
  orders?: ConciergeOrderData[];
  /** Dev info (devOptions used + effective ranking weights) for dev mode diagnostics */
  devInfo?: ConciergeDevInfo;
  onSearch?: (query: string) => void;
  onUpdateFeedback?: (messageIndex: number, feedback: { type: 'like' | 'dislike'; timestamp: string } | null) => void;
  /** Callback when a variant is selected in a FeaturedProductWidget */
  onVariantSelect?: (stacklineSku: string) => void;
  /** Callback when a suggestion pill is clicked - populates the input field */
  onSuggestionClick?: (suggestion: string) => void;
  /** Base URL for API requests. Defaults to '/api-rewards' for landing-page. */
  apiBaseUrl?: string;
  /** Whether follow-up suggestions are currently loading */
  followUpsLoading?: boolean;
  /** Called when the user clicks the Retry button on the last assistant message */
  onRetry?: () => void;
  /** True when viewing a shared thread the current user does not own (pre-fork).
   *  Used to hide the Share button since non-owners cannot share someone else's conversation.
   *  After the user sends a message (triggering a fork), this flips to false and Share reappears. */
  isSharedView?: boolean;
  /** AI SDK 6 UIMessage.parts — when present, renders via ConciergeMessagePart instead of messageRenderer */
  uiMessageParts?: ConciergeMessagePartType[];
  /** Transient status from AI SDK data parts (e.g. 'thinking' between tool calls). Only set on the streaming message. */
  transientStatus?: string | null;
  /** Current PDP SKU — when set, product widgets for this SKU render collapsed */
  pdpStacklineSku?: string | null;
  isOnChatWidget?: boolean;
  /** Brand logo URL shown in the share dialog */
  brandLogoUrl?: string;
  /** Open the shared "Share this conversation" dialog (conversationId, messageIndex for share-up-to-this-message) */
  onOpenShareDialog?: (conversationId: string, messageIndex?: number) => void;
  /** 'read-only' when a brand admin is previewing a conversation via viewToken — hides all action buttons */
  viewMode?: 'read-only';
  /** True while a human teammate is live-handling this thread. Drives the
   *  "Delivering… / Delivered / Failed" status row under user messages. */
  liveTakeoverActive?: boolean;
  /** True when this is the last user message in the thread. Combined with
   *  `liveTakeoverActive` and `isActivelyStreaming` it produces the optimistic
   *  "Delivering…" state shown before the backend confirms the Intercom 2xx. */
  isLastUserMessage?: boolean;
  /** True while the per-turn stream is open (any kind of message). Drives
   *  the optimistic "Delivering…" state on user messages during takeover —
   *  the per-message `isStreaming` flag only flips for assistant messages. */
  isActivelyStreaming?: boolean;
  /** Most recent delivery event observed for this user message via the
   *  long-lived events SSE. Bridges the gap between the per-turn stream
   *  closing (no `isActivelyStreaming` anymore) and the persisted user
   *  message being refetched from Mastra (which carries the marker on
   *  `uiMessageParts`). Only populated for the LAST user message — older
   *  ones rely on their persisted parts. */
  latestUserDeliveryFromEvents?: 'delivered' | 'failed';
}

const ChatMessageComponent = React.forwardRef<HTMLDivElement, ChatMessageProps>(
  (
    {
      role,
      content,
      bottomRef,
      isTyping = false,
      isStreaming = false,
      isLastMessage = false,
      messageIndex,
      conversationId,
      feedback,
      followUpQueries = [],
      nextUserMessage,
      orders,
      devInfo,
      onSearch,
      onUpdateFeedback,
      onVariantSelect,
      onSuggestionClick,
      apiBaseUrl,
      isSharedView,
      uiMessageParts,
      transientStatus,
      pdpStacklineSku,
      isOnChatWidget,
      brandLogoUrl,
      followUpsLoading,
      onRetry,
      onOpenShareDialog,
      viewMode,
      liveTakeoverActive,
      isLastUserMessage,
      isActivelyStreaming,
      latestUserDeliveryFromEvents,
    },
    _ref,
  ) => {
    const isUser = role === 'user';
    const hasUiMessageParts = Array.isArray(uiMessageParts) && uiMessageParts.length > 0;

    const hasAnyContent =
      content.length > 0 ||
      (hasUiMessageParts && (uiMessageParts?.some((p) => p.type !== 'text' || (p as any).text) ?? false));
    const shimmerActive = !isTyping && isStreaming && !!transientStatus && content.length === 0;

    // Initialize feedback state from props
    const [liked, setLiked] = useState(feedback?.type === 'like');
    const [disliked, setDisliked] = useState(feedback?.type === 'dislike');
    const [copied, setCopied] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
    const closePreview = useCallback(() => setPreviewImageUrl(null), []);
    const isMobile = useIsMobile();

    // Track locally selected follow-up query (for immediate UI feedback before message is sent)
    const [localSelectedFollowUp, setLocalSelectedFollowUp] = useState<string | undefined>(undefined);

    // Derive selected follow-up from next user message (if it matches one of the follow-up queries)
    const derivedSelectedFollowUp = useMemo(() => {
      if (!nextUserMessage || !followUpQueries.length) return undefined;
      // Check if any follow-up query matches the next user message
      return followUpQueries.find((query) => query === nextUserMessage);
    }, [nextUserMessage, followUpQueries]);

    // Use derived selection if available, otherwise use local selection
    const selectedFollowUp = derivedSelectedFollowUp || localSelectedFollowUp;

    // Ref and state for measuring if user message is multiline
    const userMessageRef = useRef<HTMLDivElement>(null);
    const [isMultiline, setIsMultiline] = useState(false);

    // Ref and state for shrink-wrapping assistant bubble to actual text width
    const assistantContentRef = useRef<HTMLDivElement>(null);
    const [assistantBubbleWidth, setAssistantBubbleWidth] = useState<number | undefined>(undefined);

    // Hide assistant messages whose parts all render nothing visible (e.g. suppressed tool parts).
    const messageWrapperRef = useRef<HTMLDivElement>(null);
    const [isRenderedEmpty, setIsRenderedEmpty] = useState(false);

    // True when the message contains non-text widget parts (product cards, login prompts, etc.)
    // that need to occupy full width — shrink-wrapping must be skipped in this case.
    const hasWidgetParts = useMemo(
      () =>
        Array.isArray(uiMessageParts) &&
        uiMessageParts.some(
          (p: any) =>
            ![
              'text',
              'source-url',
              'data-source-url',
              'data-follow-ups',
              'tool-call',
              'tool-result',
              'tool-search_products',
              'tool-analyze_query',
            ].includes(p.type),
        ),
      [uiMessageParts],
    );

    // Measure the user message to determine if it's multiline using getClientRects()
    // This is the most reliable method as it counts actual rendered line boxes
    useLayoutEffect(() => {
      if (!isUser || !userMessageRef.current) return;

      const el = userMessageRef.current;
      const textNode = el.firstChild;

      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        // For text nodes, use Range.getClientRects() to count lines
        const range = document.createRange();
        range.selectNodeContents(el);
        const rects = range.getClientRects();
        // Each rect represents a line box - more than 1 means multiline
        setIsMultiline(rects.length > 1);
      } else {
        // Fallback: compare scrollHeight to single line height
        const computedStyle = window.getComputedStyle(el);
        const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
        // If content height exceeds ~1.5 lines, it's multiline
        setIsMultiline(el.scrollHeight > lineHeight * 1.5);
      }
    }, [isUser, content]);

    // Measure assistant bubble text to shrink-wrap the bubble to the widest rendered line.
    // This eliminates the visual gap when the last line of wrapping text is shorter.
    // Skip for messages that contain widget parts — those need the full container width.
    useLayoutEffect(() => {
      if (isUser || isTyping || hasWidgetParts) {
        setAssistantBubbleWidth(undefined);
        return;
      }
      const el = assistantContentRef.current;
      if (!el) {
        setAssistantBubbleWidth(undefined);
        return;
      }

      // Walk all text nodes and measure their rendered line rects
      const containerLeft = el.getBoundingClientRect().left;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let maxRight = 0;
      let node: Node | null;
      while ((node = walker.nextNode())) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const rects = range.getClientRects();
        for (let i = 0; i < rects.length; i++) {
          maxRight = Math.max(maxRight, rects[i].right);
        }
      }

      if (maxRight > 0) {
        const textWidth = maxRight - containerLeft;
        const computedStyle = window.getComputedStyle(el);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
        const neededWidth = Math.ceil(textWidth) + paddingLeft + paddingRight + 2;

        // Only shrink if the text is meaningfully narrower than the container
        if (neededWidth < el.offsetWidth - 8) {
          setAssistantBubbleWidth(neededWidth);
        } else {
          setAssistantBubbleWidth(undefined);
        }
      } else {
        setAssistantBubbleWidth(undefined);
      }
    }, [isUser, isTyping, content, hasUiMessageParts, hasWidgetParts]);

    useLayoutEffect(() => {
      if (isUser || isTyping || isStreaming || hasWidgetParts) {
        setIsRenderedEmpty(false);
        return;
      }
      const el = assistantContentRef.current;
      if (!el) return;
      const hasVisibleChild =
        (el.textContent?.trim().length ?? 0) > 0 ||
        el.querySelector(
          'img, svg, canvas, video, iframe, [class*="phc-"], [class*="concierge-product"], [class*="concierge-section"]',
        ) !== null;
      setIsRenderedEmpty(!hasVisibleChild);
    }, [isUser, isTyping, isStreaming, content, hasUiMessageParts, uiMessageParts, hasWidgetParts]);

    const formatProductForCopy = (product: SectionBasedProduct, isFeatured: boolean): string => {
      const parts = [`Title: ${product.title}`];

      if (product.brandName) {
        parts.push(`Brand: ${product.brandName}`);
      }

      if (product.retailPrice !== undefined) {
        parts.push(`Price: $${product.retailPrice.toFixed(2)}`);
      }

      // Construct the product URL. Back-room (App Proxy sub-path): the
      // Brandclub PDP route doesn't exist on the storefront (the proxy only
      // serves /concierge*), so prefer the product-page URL the payload
      // already carries — the store's own /products/<handle> page (D-20).
      // Front-room (routePath ''): the Brandclub PDP link, unchanged.
      const routePath = getRoutePath();
      const url =
        routePath && product.retailerProductPageUrl
          ? product.retailerProductPageUrl
          : `${window.location.origin}${routePath}/product/p/${product.stacklineSku}/product`;
      parts.push(`URL: ${url}`);

      // Add feature bullets for featured products
      if (isFeatured && product.featureBullets && product.featureBullets.length > 0) {
        parts.push(`\nFeatures:`);
        product.featureBullets.forEach((bullet) => {
          parts.push(`• ${bullet}`);
        });
      }

      return parts.join('\n');
    };

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (error) {
        toast.error('Failed to copy message');
      }
    };

    const handleShare = () => {
      if (!conversationId) {
        console.warn('Cannot share: missing conversationId');
        return;
      }
      onOpenShareDialog?.(conversationId, messageIndex);
    };

    const handleLike = async () => {
      if (!conversationId || messageIndex === undefined) return;

      const newState = !liked;
      setLiked(newState);
      setDisliked(false);

      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? 'like' : null,
          headersToInject: headers,
          apiBaseUrl,
        });
        if (onUpdateFeedback && result.message.metadata?.feedback) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error('Failed to update feedback:', error);
        setLiked(!newState);
      }
    };

    const handleDislike = async () => {
      if (!conversationId || messageIndex === undefined) return;

      const newState = !disliked;
      setDisliked(newState);
      setLiked(false);

      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? 'dislike' : null,
          headersToInject: headers,
          apiBaseUrl,
        });
        if (onUpdateFeedback && result.message.metadata?.feedback) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error('Failed to update feedback:', error);
        setDisliked(!newState);
      }
    };

    const handleFollowUpClick = (query: string) => {
      // Don't allow clicking if not the last message or already selected
      if (!isLastMessage || selectedFollowUp) return;

      // Set local selection immediately for responsive UI
      setLocalSelectedFollowUp(query);

      // Auto-send the query
      onSearch?.(query);
    };

    // Helper to determine if message is short (for pill shape)
    const _isShortMessage = (text: string, threshold: number) => {
      return !text.includes('\n') && text.length < threshold;
    };

    // Helper to get flex justify-content value from alignment
    const getJustifyContent = (alignment: 'left' | 'right' | 'center') => {
      switch (alignment) {
        case 'right':
          return 'flex-end';
        case 'center':
          return 'center';
        default:
          return 'flex-start';
      }
    };

    // Derive the delivery status for the user-message bubble during live
    // takeover. The mirrored / delivery-failed markers are persisted inline
    // on the user message's parts (see handleHumanTakeoverTurn). When neither
    // is present yet but the user just submitted (this is the LAST user
    // message AND the per-turn stream is still open AND takeover is active),
    // show the optimistic "Delivering…" state.
    const userDeliveryStatus = useMemo<'delivered' | 'failed' | 'pending' | undefined>(() => {
      if (!isUser || !liveTakeoverActive) return undefined;
      // 1. Persisted markers on this message win (authoritative — these
      //    arrive after a Mastra refetch / page reload).
      const parts = (uiMessageParts ?? []) as Array<{ type: string }>;
      if (parts.some((p) => p.type === 'data-user-message-mirrored')) return 'delivered';
      if (parts.some((p) => p.type === 'data-user-message-delivery-failed')) return 'failed';
      // 2. Live event bridge — the long-lived events SSE delivers the
      //    mirrored / delivery-failed event for the LATEST user message
      //    within the same tick the backend publishes it, so we never
      //    show a blank gap between "Delivering…" and "Delivered".
      if (latestUserDeliveryFromEvents === 'delivered') return 'delivered';
      if (latestUserDeliveryFromEvents === 'failed') return 'failed';
      // 3. Optimistic pending — the per-turn stream is still open, the
      //    backend hasn't confirmed the Intercom 2xx yet. Once the bus
      //    publishes the mirrored / failed event (same tick as the SSE
      //    write on the per-turn stream), step 2 above takes over before
      //    `isActivelyStreaming` even flips false.
      if (isLastUserMessage && isActivelyStreaming) return 'pending';
      return undefined;
    }, [isUser, liveTakeoverActive, uiMessageParts, isLastUserMessage, isActivelyStreaming, latestUserDeliveryFromEvents]);

    const handleRetryDelivery = useCallback(() => {
      if (!onSearch || !content) return;
      onSearch(content);
    }, [onSearch, content]);

    // D-33 — brand can override the delivery success copy from Concierge
    // Studio → Branding → Support. Defaults to "Delivered to Support Team".
    const supportCopy = useSupportCopy();

    const renderUserDeliveryStatus = () => {
      if (!userDeliveryStatus) return null;
      // Right-align under the user bubble (which is itself right-aligned).
      const baseClasses =
        'tw:mt-1 tw:flex tw:items-center tw:justify-end tw:gap-1.5 tw:text-xs tw:leading-tight';
      if (userDeliveryStatus === 'delivered') {
        return (
          <div
            className={baseClasses}
            style={{ color: 'var(--bc-support-text)' }}
            role="status"
            aria-label={`Message delivered to ${supportCopy.teamLabel.toLowerCase()}`}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 8.5l3 3 7-7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{supportCopy.deliveredCopy}</span>
          </div>
        );
      }
      if (userDeliveryStatus === 'failed') {
        return (
          <div
            className={`${baseClasses} tw:text-red-600`}
            role="status"
            aria-label="Message failed to deliver to support team"
          >
            <span aria-hidden="true">✗</span>
            <span>Failed to deliver</span>
            {onSearch && content && (
              <>
                <span aria-hidden="true" className="tw:text-gray-400">
                  ·
                </span>
                <button
                  type="button"
                  onClick={handleRetryDelivery}
                  className="tw:cursor-pointer tw:font-semibold tw:text-red-700 tw:underline tw:underline-offset-2 hover:tw:text-red-800"
                >
                  Retry
                </button>
              </>
            )}
          </div>
        );
      }
      return (
        <div className={`${baseClasses} tw:text-gray-500`} role="status" aria-label="Delivering message">
          <span>Delivering…</span>
        </div>
      );
    };

    // Render user message bubble with widget styling
    const renderUserMessageBubble = () => (
      <MessageBubbleWidget
        widgetId="concierge-user-message"
        displayName="User Message Bubble"
        initialState={{
          bgColor: customColor(brandclub_colors.blueWhite),
          textColor: themeColor('textPrimary'),
          maxWidth: '90%',
          alignment: 'right',
        }}
      >
        {(styles: MessageBubbleWidgetState & { fontFamilyCss?: string }, resolveColor) => {
          // Use measured isMultiline state for reliable multiline detection
          const borderRadius = isMultiline ? styles.borderRadius : styles.borderRadiusShort;

          // Determine font-family: use fontFamilyCss from widget if available, otherwise fallback to CSS variable
          const fontFamily = styles.fontFamilyCss
            ? styles.fontFamilyCss !== 'inherit'
              ? styles.fontFamilyCss
              : 'var(--concierge-user-message-font-family, inherit)'
            : 'var(--concierge-user-message-font-family, inherit)';

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: getJustifyContent(styles.alignment),
                width: '100%',
              }}
            >
              <div style={{ maxWidth: styles.maxWidth }}>
                <div
                  className="message-content user-message"
                  style={{
                    maxWidth: '100%',
                    minWidth: '50px',
                    backgroundColor: resolveColor(styles.bgColor),
                    color: resolveColor(styles.textColor),
                    borderRadius: `${borderRadius}px`,
                    wordWrap: 'break-word',
                    padding: `${styles.paddingY}px ${styles.paddingX}px`,
                    // Per design, the user-message bubble drops to 14px on
                    // mobile. The widget-configured fontSize is retained on
                    // desktop so editor overrides still apply there.
                    fontSize: `${isMobile ? 14 : styles.fontSize}px`,
                    fontWeight: styles.fontWeight,
                    fontFamily: fontFamily,
                    lineHeight: styles.lineHeight,
                    ...(!isMultiline && styles.singleLineHeight > 0
                      ? {
                          minHeight: styles.singleLineHeight,
                          display: 'flex',
                          alignItems: 'center',
                          boxSizing: 'border-box' as const,
                        }
                      : {}),
                  }}
                >
                  <div
                    ref={userMessageRef}
                    style={{
                      whiteSpace: 'pre-wrap',
                      // The single-line branch above turns the bubble into a
                      // flex container, and a flex item defaults to
                      // `min-width: auto` — it refuses to shrink below its
                      // min-content width, so text ran past the bubble's
                      // rounded edge instead of wrapping (worst on a narrow
                      // portrait viewport). `min-width: 0` lets it shrink;
                      // `anywhere` also lets an unbroken string (a URL, a long
                      // SKU) break rather than push the bubble wide.
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {content}
                  </div>
                </div>
                {renderUserDeliveryStatus()}
              </div>
            </div>
          );
        }}
      </MessageBubbleWidget>
    );

    // Render assistant message bubble with widget styling
    const renderAssistantMessageBubble = () => (
      <MessageBubbleWidget
        widgetId="concierge-assistant-message"
        displayName="Assistant Message Bubble"
        initialState={{
          bgColor: themeColor('background'),
          textColor: themeColor('textPrimary'),
          maxWidth: '100%',
          paddingX: 0,
          paddingY: 0,
          alignment: 'left',
        }}
      >
        {(styles: MessageBubbleWidgetState, resolveColor) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: getJustifyContent(styles.alignment),
              width: '100%',
            }}
          >
            <div
              style={{
                maxWidth: styles.maxWidth,
                width: hasWidgetParts ? '100%' : assistantBubbleWidth ?? undefined,
                color: resolveColor(styles.textColor),
              }}
            >
              <div
                ref={assistantContentRef}
                className="message-content"
                style={{
                  maxWidth: '100%',
                  minWidth: '50px',
                  backgroundColor: resolveColor(styles.bgColor),
                  color: resolveColor(styles.textColor),
                  borderRadius: `${styles.borderRadius}px`,
                  wordWrap: 'break-word',
                  padding: `${styles.paddingY}px ${styles.paddingX}px`,
                  fontSize: `${styles.fontSize}px`,
                  fontWeight: styles.fontWeight,
                  lineHeight: styles.lineHeight,
                }}
              >
                {hasUiMessageParts ? (
                  <ConciergeMessagePart
                    parts={uiMessageParts}
                    onProductClick={onVariantSelect}
                    isStreaming={isStreaming}
                    pdpStacklineSku={pdpStacklineSku}
                    isOnChatWidget={isOnChatWidget}
                    apiBaseUrl={apiBaseUrl}
                  />
                ) : (
                  <div
                    className="message-markdown"
                    style={{ display: 'flex', flexDirection: 'column' }}
                    dangerouslySetInnerHTML={{ __html: marked.parse(content) as string }}
                  />
                )}
                {/* Pulsing dot cursor removed — "Thinking..." shimmer is shown instead */}
                {/* Render order cards for purchase history responses */}
                {orders && orders.length > 0 && <OrderCardList orders={orders} />}
              </div>

              {bottomRef && (
                <div
                  style={{
                    height: '1px',
                    width: '100%',
                  }}
                  ref={bottomRef}
                />
              )}

              {/* Action buttons for assistant messages */}
              {viewMode !== 'read-only' && !isTyping && !isStreaming && content.length > 0 ? (
                <Actions className="tw:mt-2">
                  <Action
                    label="Copy"
                    tooltip={copied ? 'Response copied!' : 'Copy response'}
                    tooltipOpen={copied || undefined}
                    onClick={handleCopy}
                  >
                    <CopyIcon width={20} height={20} />
                  </Action>
                  <Action
                    label="Like"
                    tooltip={liked ? 'Thanks for your feedback!' : 'Good response'}
                    onClick={handleLike}
                    className={liked ? 'tw:bg-[#DCDDDC]' : ''}
                  >
                    <ThumbsUpIcon width={20} height={20} className={liked ? 'tw:fill-current' : ''} />
                  </Action>
                  <Action
                    label="Dislike"
                    tooltip={disliked ? 'Thanks for your feedback!' : 'Bad response'}
                    onClick={handleDislike}
                    className={disliked ? 'tw:bg-[#DCDDDC]' : ''}
                  >
                    <ThumbsDownIcon width={20} height={20} className={disliked ? 'tw:fill-current' : ''} />
                  </Action>
                  {!isSharedView && !isOnChatWidget && (
                    <Action label="Share" tooltip="Share" onClick={handleShare}>
                      <ShareIcon width={20} height={20} />
                    </Action>
                  )}
                  {isLastMessage && !isSharedView && onRetry && (
                    <Action label="Retry" tooltip="Try again" onClick={() => onRetry()}>
                      <RetryIcon />
                    </Action>
                  )}
                </Actions>
              ) : null}

              {/* Follow-ups loading indicator */}
              {followUpsLoading && (
                <div
                  style={{
                    padding: '4px 0 8px',
                    color: 'var(--color-text-secondary, #9ca3af)',
                    fontSize: '13px',
                    letterSpacing: '0.1em',
                  }}
                >
                  ...
                </div>
              )}

              {/* Follow-up suggestions for assistant messages */}
              {/* Hide follow-ups entirely if user sent their own message (didn't click a follow-up) */}
              {!isTyping && !isStreaming && followUpQueries.length > 0 && !(nextUserMessage && !selectedFollowUp) && (
                <FollowUpQueriesWidget widgetId="concierge-follow-up-queries" displayName="Follow-up Suggestions">
                  {(widgetStyles: FollowUpQueriesWidgetState, resolveColor) => {
                    const isDisabled = !isLastMessage || !!selectedFollowUp;

                    const hex = (c: string) => (!c || c.startsWith('#') || c.startsWith('rgb') ? c : `#${c}`);

                    const defaultBg = hex(resolveColor(widgetStyles.pillBgColor));
                    const defaultText = hex(resolveColor(widgetStyles.pillTextColor));
                    const defaultBorder = hex(resolveColor(widgetStyles.pillBorderColor));
                    const hoverBg = defaultText;
                    const hoverText = defaultBg;
                    const hoverBorder = defaultText;
                    const selectedBg = hex(resolveColor(widgetStyles.pillBgColorSelected));
                    const selectedText = hex(resolveColor(widgetStyles.pillTextColorSelected));
                    const selectedBorder = hex(resolveColor(widgetStyles.pillBorderColorSelected));

                    return (
                      <div
                        className="fuq-wrap"
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          marginTop: 16,
                          gap: `${widgetStyles.containerGap}px`,
                          width: '100%',
                        }}
                      >
                        <style>{`
                          .fuq-wrap .fuq-pill {
                            background-color: ${defaultBg};
                            color: ${defaultText};
                            border-color: ${defaultBorder};
                            transition: all 0.2s ease;
                          }
                          .fuq-wrap .fuq-pill:not(:disabled):not(.fuq-selected):hover {
                            background-color: ${hoverBg};
                            color: ${hoverText};
                            border-color: ${hoverBorder};
                          }
                          .fuq-wrap .fuq-pill.fuq-selected {
                            background-color: ${selectedBg};
                            color: ${selectedText};
                            border-color: ${selectedBorder};
                          }
                        `}</style>
                        {followUpQueries.map((query, index) => {
                          const isSelected = selectedFollowUp === query;

                          return (
                            <button
                              key={index}
                              className={`fuq-pill${isSelected ? ' fuq-selected' : ''}`}
                              onClick={() => handleFollowUpClick(query)}
                              disabled={isDisabled && !isSelected}
                              style={{
                                padding: `${widgetStyles.pillPaddingY}px ${widgetStyles.pillPaddingX}px`,
                                borderRadius: `${widgetStyles.pillBorderRadius}px`,
                                borderWidth: widgetStyles.pillBorderWidth,
                                borderStyle: 'solid',
                                fontSize: `${widgetStyles.pillFontSize}px`,
                                fontWeight: widgetStyles.pillFontWeight,
                                lineHeight: '22px',
                                textAlign: 'left',
                                cursor: isSelected ? 'default' : isDisabled ? 'not-allowed' : 'pointer',
                                opacity: !isSelected && isDisabled ? 0.5 : 1,
                              }}
                            >
                              {query}
                            </button>
                          );
                        })}
                      </div>
                    );
                  }}
                </FollowUpQueriesWidget>
              )}
            </div>
          </div>
        )}
      </MessageBubbleWidget>
    );

    if (!isTyping && !hasAnyContent && !shimmerActive) return null;

    return (
      <div
        ref={messageWrapperRef}
        className={`message ${role}`}
        style={{
          display: isRenderedEmpty ? 'none' : 'flex',
          flexDirection: 'column',
          width: '100%',
          marginBottom: hasWidgetParts ? 16 : undefined,
        }}
      >
        {/* Dev Info Panel - shows devOptions and effective ranking weights (dev mode only) */}
        {!isUser && devInfo && <DevInfoPanel devInfo={devInfo} />}
        {/* Pulsing dot — shown as initial typing indicator before "Thinking..." appears */}
        {!isUser && isTyping && (
          <ChainOfThoughtWidget widgetId="chain-of-thought" displayName="Chain of Thought">
            {(styles: ChainOfThoughtWidgetState, resolveColor) => (
              <div style={{ padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px` }}>
                <style>{`@keyframes cc-pulse{0%,100%{transform:scale(1)}50%{transform:scale(0.5)}}`}</style>
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    backgroundColor: resolveColor(styles.thinkingTextColor),
                    borderRadius: '50%',
                    animation: 'cc-pulse 1s ease-in-out infinite',
                  }}
                />
              </div>
            )}
          </ChainOfThoughtWidget>
        )}
        {/* "Thinking..." shimmer — shown while streaming with a transient status (after the dot phase) */}
        {!isUser && shimmerActive && (
          <ChainOfThoughtWidget widgetId="chain-of-thought" displayName="Chain of Thought">
            {(styles: ChainOfThoughtWidgetState, resolveColor) =>
              styles.thinkingEnabled ? (
                <div style={{ maxWidth: '100%' }}>
                  <div
                    className="message-content"
                    style={{
                      maxWidth: '100%',
                      backgroundColor: 'none',
                      color: resolveColor(styles.thinkingTextColor),
                      borderRadius: `${styles.thinkingBorderRadius}px`,
                      borderBottomLeftRadius: `${styles.thinkingBorderRadiusBottomLeft}px`,
                      borderBottomRightRadius: `${styles.thinkingBorderRadius}px`,
                      wordWrap: 'break-word',
                      padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px`,
                    }}
                  >
                    <Shimmer
                      as="p"
                      style={{ fontSize: `${styles.thinkingTextFontSize}px`, fontFamily: 'inherit' }}
                      duration={styles.shimmerDuration}
                      color={resolveColor(styles.thinkingTextColor)}
                      highlightColor={resolveColor(styles.shimmerColor)}
                    >
                      {transientStatus && transientStatus !== 'thinking' ? transientStatus : styles.thinkingText}
                    </Shimmer>
                  </div>
                </div>
              ) : null
            }
          </ChainOfThoughtWidget>
        )}

        {/* User message file parts (images) - render ABOVE the bubble */}
        {isUser &&
          hasUiMessageParts &&
          (() => {
            const imageFiles = uiMessageParts!.filter(
              (p): p is Extract<typeof p, { type: 'file' }> => p.type === 'file' && p.mediaType?.startsWith('image/'),
            );
            if (imageFiles.length === 0) return null;
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  width: '100%',
                  marginBottom: '0.5rem',
                }}
              >
                {imageFiles.map((fp, i) => {
                  const size = imageFiles.length === 1 ? (isMobile ? 250 : 256) : isMobile ? 96 : 128;
                  return (
                    <div
                      key={i}
                      onClick={() => setPreviewImageUrl(fp.url)}
                      style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#f9f9f9',
                        cursor: 'zoom-in',
                      }}
                    >
                      <img
                        src={fp.url}
                        alt={fp.filename || 'Uploaded image'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          mixBlendMode: 'multiply',
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })()}
        {/* Message content */}
        {!isTyping && (isUser ? renderUserMessageBubble() : renderAssistantMessageBubble())}
        {previewImageUrl && <ImagePreviewOverlay src={previewImageUrl} onClose={closePreview} />}
      </div>
    );
  },
);

// Produces a stable fingerprint of uiMessageParts that changes when parts are added,
// when any part transitions between states (e.g. partial-call → call → output-available),
// or when text content grows during streaming.
function uiPartsFingerprint(parts: ChatMessageProps['uiMessageParts']): string {
  if (!parts?.length) return '';
  return `${parts.length}:${parts
    .map((p: any) => {
      if (p.type === 'text') return `text|${p.text?.length ?? 0}`;
      if (p.type === 'data-product-carousels' || p.type === 'data-section') return `ds|${p.data?.title ?? ''}`;
      return `${p.type}|${p.state ?? ''}`;
    })
    .join(',')}`;
}

// Memoize the component to prevent unnecessary re-renders
export const ChatMessage = React.memo(ChatMessageComponent, (prevProps, nextProps) => {
  // Custom comparison function to prevent re-renders when props haven't meaningfully changed
  return (
    prevProps.role === nextProps.role &&
    prevProps.content === nextProps.content &&
    prevProps.isTyping === nextProps.isTyping &&
    prevProps.bottomRef === nextProps.bottomRef &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.isLastMessage === nextProps.isLastMessage &&
    prevProps.followUpQueries?.length === nextProps.followUpQueries?.length &&
    prevProps.nextUserMessage === nextProps.nextUserMessage &&
    prevProps.orders === nextProps.orders &&
    prevProps.onSearch === nextProps.onSearch &&
    prevProps.onVariantSelect === nextProps.onVariantSelect &&
    prevProps.onSuggestionClick === nextProps.onSuggestionClick &&
    prevProps.liveTakeoverActive === nextProps.liveTakeoverActive &&
    prevProps.isLastUserMessage === nextProps.isLastUserMessage &&
    prevProps.isActivelyStreaming === nextProps.isActivelyStreaming &&
    prevProps.latestUserDeliveryFromEvents === nextProps.latestUserDeliveryFromEvents &&
    uiPartsFingerprint(prevProps.uiMessageParts) === uiPartsFingerprint(nextProps.uiMessageParts)
  );
});
